import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from 'src/components/button';
import { Section } from 'src/components/section';
import { selectUserLoggedIn } from 'src/store/slices/user-slice';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { selectBuilding } from 'src/store/slices/booking-slice';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { getTheme } from 'src/utils/getTheme';

import { AddressDetails } from './address-details';
import { styles } from './styles';
import { UserDetails } from './user-details';
import { SpanishTaxDetails } from './spanish-tax-details';
import { userDataKeys } from './constants';

import type { DetailsFormProps, UserDataFormValues } from './types';

interface ExtendedDetailsFormProps extends DetailsFormProps<UserDataFormValues> {
    loading: boolean;
}

export const DetailsForm: FC<ExtendedDetailsFormProps> = ({
    control,
    onFormSubmit,
    hasRoommates,
    loading,
}) => {
    const { t } = useTranslation();
    const {
        formState: { isValid },
        handleSubmit,
        getValues,
    } = useFormContext<UserDataFormValues>();

    const { brand } = getTheme();

    const userLoggedIn = useSelector(selectUserLoggedIn);
    const building = useSelector(selectBuilding);

    const getCtaName = () => {
        const loggedIn = userLoggedIn ? 'logged_in_' : '';
        const roommates = hasRoommates ? 'roommates' : 'agreements';

        return `your_details_form_cta_${loggedIn}${roommates}`;
    };

    const onSubmit = handleSubmit(() => {
        const isConfirmed = getValues(userDataKeys.userFormConfirmation);
        if (isConfirmed && isValid) {
            onFormSubmit();
        }
    });

    if (!building) throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);

    return (
        <Section header={t('your_details_form_title')} style={styles.container}>
            <UserDetails control={control} />
            <AddressDetails control={control} />
            {building.spanish_tax_applicable && <SpanishTaxDetails control={control} />}

            <ControlledField
                control={control}
                name={userDataKeys.userFormConfirmation}
                label={t('your_details_form_confirmation_label', {
                    brand,
                })}
                fieldType={FieldTypes.Checkbox}
                required
            />

            <Button variant="contained" onClick={onSubmit} css={styles.cta} loading={loading}>
                {t(getCtaName())}
            </Button>
        </Section>
    );
};
